import { createContext, useContext } from "react";

export interface UpgradeModalContextValue {
    /**
     * Open the upgrade modal with an optional funnel source
     * @param fsrc - Funnel source for analytics tracking (e.g., "rapid-out-of-credits", "rapid-private-data")
     */
    openUpgradeModal: (fsrc?: string) => void;

    /**
     * Close the upgrade modal
     */
    closeUpgradeModal: () => void;

    /**
     * Check if the upgrade modal is currently open
     */
    isUpgradeModalOpen: boolean;
}

export const UpgradeModalContext = createContext<UpgradeModalContextValue | undefined>(undefined);

/**
 * Hook to access upgrade modal controls from any component
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { openUpgradeModal } = useUpgradeModal();
 *
 *   return (
 *     <button onClick={() => openUpgradeModal("my-feature")}>
 *       Upgrade to unlock
 *     </button>
 *   );
 * }
 * ```
 */
export function useUpgradeModal(): UpgradeModalContextValue {
    const context = useContext(UpgradeModalContext);
    if (!context) {
        throw new Error("useUpgradeModal must be used within an UpgradeModalProvider");
    }
    return context;
}
